// store.js
/* eslint no-shadow: ["error", { "allow": ["state"] }] */

import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import createPersistedState from 'vuex-persistedstate';

import {
  ADD_ITEM,
  EDIT_ITEM,
  GET_ITEM,
  REMOVE_ITEM,
  CHECKOUT_FAILURE,
  CHECKOUT_SUCCESS,
  CHECKOUT_REDIRECT,
} from './mutation-types';

Vue.use(Vuex);

// the root, initial state object
const state = {
  // cartItems: !!localStorage.getItem('vuex.cartitems') || [],
  cartItems: [],
  glove: !!localStorage.getItem('vuex.glove') || {
    gender: 'M',
    size: 8,
    quantity: 1,
    color: null,
    colorLabelFR: '',
    colorLabelEN: '',
  },
  billingAddress: !!localStorage.getItem('vuex.billing') || {
    gender: 'M',
    fname: '',
    lname: '',
    company: '',
    vat: '',
    address: '',
    postcode: '',
    city: '',
    country: 'fr',
    phone: '',
    email: '',
    email_confirm: '',
  },
  useSameAddress: !!localStorage.getItem('vuex.use_same_address') || false,
  shippingAddress: !!localStorage.getItem('vuex.shipping') || {
    gender: 'M',
    fname: '',
    lname: '',
    company: '',
    address: '',
    postcode: '',
    city: '',
    country: 'fr',
    observation: '',
    phone: '',
    is_gift: false,
    giftMessage: '',
  },
  checkout: !!localStorage.getItem('vuex.checkout') || {
    transaction: '',
    order_no: '',
  },
};


// define the possible mutations that can be applied to our state
const cartMutations = {
  [ADD_ITEM](state, item) {
    const existing = state.cartItems.find(i => i.name === item.name);

    if (!existing) {
      state.cartItems.push(item);
    } else {
      const newQuantity = Number(existing.quantity) + item.quantity;
      existing.quantity = Math.min(10, newQuantity);
    }
  },
  [GET_ITEM](state, item) {
    let idx = 0;
    let currentItem;

    for (; idx < state.cartItems.length; idx += 1) {
      currentItem = state.cartItems[idx];
      if (currentItem.name !== item.name) {
        return currentItem;
      }
    }
    return null;
  },
  [EDIT_ITEM](state, item) {
    state.cartItems.map((i) => {
      if (i.name === item.name) {
        return (Object.assign(i, { quantity: item.quantity }));
      }
      return i;
    });
  },
  [REMOVE_ITEM](state, item) {
    // Perf: https://jsperf.com/splice-vs-filter (old)
    // state.cartItems.splice(state.cartItems.filter(cartItem => cartItem.name === item.name), 1);
    const res = [];
    let idx = 0;
    let currentItem;

    for (; idx < state.cartItems.length; idx += 1) {
      currentItem = state.cartItems[idx];
      if (currentItem.name !== item.name) {
        res.push(currentItem);
      }
    }
    state.cartItems = res;
  },
};


const addressMutations = {
  updateBillingAddress(state, newAddress) {
    Object.assign(state.billingAddress, newAddress);
  },
  updateShippingAddress(state, newAddress) {
    Object.assign(state.shippingAddress, newAddress);
  },
  updateSelectedGlove(state, { field, value }) {
    Object.assign(state.glove, {
      [field]: value,
    });
  },
  copyBillingAddress(state) {
    Object.assign(state.shippingAddress,
      state.billingAddress);
  },
};

const checkoutMutations = {
  [CHECKOUT_REDIRECT](state, { transaction, order_no, total, hash }) {
    state.checkout.order_no = order_no;
    state.checkout.transaction = transaction;
    state.checkout.total = total;
    state.checkout.hash = hash;
  },
  [CHECKOUT_FAILURE]() {
    // console.error (err);
  },
  [CHECKOUT_SUCCESS]() {
    // console.log (err);
  },
};

const UserGetters = {
  billingAddress: state => state.billingAddress,
  shippingAddress: state => state.shippingAddress,
  glove: state => state.glove,
};

const CartGetters = {
  items: state => state.cartItems,
  totalPrice: state => state.cartItems.reduce((t, item) => t + (item.price * item.quantity), 0),
  totalVAT: (state, allGetters) => allGetters.totalPrice - allGetters.totalNoVAT,
  totalNoVAT: state => Math.round(state.cartItems.reduce((total, item) =>
    total + ((item.quantity * item.price) / 1.20), 0), 2),
  nbItems: state => state.cartItems.length,
  nbTotalItems: state => state.cartItems.reduce((t, item) => t + item.quantity, 0),
  grandTotal: (state, allGetters) => allGetters.totalPrice + allGetters.shippingCosts,
  totalNoVATWithShipping: (state, allGetters) =>
    allGetters.totalNoVAT + (allGetters.shippingCosts - allGetters.shippingCostsVAT),
  totalVATWithShipping: (state, allGetters) => allGetters.grandTotal
    - allGetters.totalNoVATWithShipping,
};

const ShippingGetters = {
  shippingCosts: (state, allGetters) => (allGetters.nbTotalItems > 4 ? 5000 : 3000),
  shippingCostsNoVat: (state, allGetters) => Math.round(allGetters.shippingCosts / 1.20, 2),
  shippingCostsVAT: (state, allGetters) => allGetters.shippingCosts - allGetters.shippingCostsNoVat,
};

/**
 * Asynchronous actions
 * */
const cartActions = {
  validateCart({ commit, state, dispatch }, params) {
    const actionUrl = `${process.env.API_URL}orders`;
    const data = {
      fact_gender: params.billingAddress.gender,
      fact_fname: params.billingAddress.fname,
      fact_lname: params.billingAddress.lname,
      fact_company: params.billingAddress.company,
      fact_vat: params.billingAddress.vat,
      fact_address: params.billingAddress.address,
      fact_postcode: params.billingAddress.postcode,
      fact_city: params.billingAddress.city,
      fact_country: params.billingAddress.country,
      fact_phone: params.billingAddress.phone,
      fact_email: params.billingAddress.email,
      livr_gender: params.shippingAddress.gender,
      livr_fname: params.shippingAddress.fname,
      livr_lname: params.shippingAddress.lname,
      livr_company: params.shippingAddress.company,
      livr_address: params.shippingAddress.address,
      livr_postcode: params.shippingAddress.postcode,
      livr_city: params.shippingAddress.city,
      livr_country: params.shippingAddress.country,
      livr_phone: params.shippingAddress.phone,
      livr_observation: params.shippingAddress.observation,
      livr_message: params.shippingAddress.giftMessage,
      articles: params.cartItems,
      total_vat: params.totalVAT,
      total_no_vat: params.totalNoVAT,
      total_with_vat: params.totalPrice,
      total_shipping: params.shippingCosts,
      grand_total: params.grandTotal,
    };
    //

    axios.post(actionUrl, data)
    .then((resp) => {
      // Let's assume it won't fail => reset orde
      console.warn('from store : ', resp.data);
      console.log(resp.data);
      commit('CHECKOUT_REDIRECT', resp.data);
      dispatch('redirectPaymentAndReset');
    })
    .catch((err) => {
      commit(CHECKOUT_FAILURE, err);
    });
  },
  redirectPaymentAndReset({ state }) {
    const paymentUrl = `${process.env.PAYMENT_URL}`;
    const data = {
      transaction: state.checkout.transaction,
      order_no: state.checkout.order_no,
      total: state.checkout.total,
      hash: state.checkout.hash,
      email: state.billingAddress.email,
    };

    const url = new URL(`${paymentUrl}?transaction=${data.transaction}&order_no=${data.order_no}&total=${data.total}&hash=${data.hash}&email=${data.email}`);
    window.setTimeout(() => { window.location.href = url; }, 1000);
  },
};


// create the Vuex instance by combining the state and mutations objects
// then export the Vuex store for use by our components
export default new Vuex.Store({
  state,
  mutations: Object.assign({}, cartMutations, addressMutations, checkoutMutations),
  actions: Object.assign({}, cartActions),
  getters: Object.assign({}, UserGetters, CartGetters, ShippingGetters),
  plugins: [createPersistedState()],
});




// WEBPACK FOOTER //
// ./src/vuex/store.js